<template>
  <v-main>
    <v-container fluid>
      <!-- Header Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <v-btn
                class="mx-2"
                fab
                x-small
                color="primary"
                @click="$router.back()"
              >
                <v-icon dark>mdi-arrow-left</v-icon>
              </v-btn>
              <h3 class="font-weight-bold">Vehicle KYC Documents</h3>
              <v-spacer></v-spacer>
              <!-- User Filter Indicator -->
              <v-chip
                v-if="filter.user_id"
                color="indigo"
                text-color="white"
                close
                @click:close="clearUserFilter"
              >
                <v-icon left>mdi-filter</v-icon>
                Filtered by User ID: {{ filter.user_id }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="4">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search by vehicle number, license, RC..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col md="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.is_verified"
                    :items="verificationStatuses"
                    label="Verification Status"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-shield-check"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col md="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="activeStatuses"
                    label="Active Status"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-toggle-switch"
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col md="2">
                  <v-btn
                    rounded
                    block
                    color="primary"
                    @click="getData()"
                    height="48"
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Refresh
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
                <v-skeleton-loader
                  class="mx-auto mt-5"
                  type="table"
                ></v-skeleton-loader>
              </section>

              <section v-else>
                <v-data-table
                  v-if="documents.length > 0"
                  :headers="headers"
                  :items="documents"
                  :items-per-page="itemsPerPage"
                  hide-default-footer
                  class="elevation-0"
                >
                  <!-- User Info -->
                  <template v-slot:[`item.user`]="{ item }">
                    <div class="d-flex align-center py-2">
                      <v-avatar size="40" class="mr-2">
                        <v-img
                          v-if="item.user && item.user.image_path"
                          :src="item.user.image_path"
                        ></v-img>
                        <v-icon v-else>mdi-account-circle</v-icon>
                      </v-avatar>
                      <div>
                        <div class="font-weight-medium">
                          {{
                            item.user
                              ? `${item.user.first_name} ${item.user.last_name}`
                              : "N/A"
                          }}
                        </div>
                        <div class="text-caption text--secondary">
                          {{
                            item.user && item.user.position
                              ? item.user.position.name
                              : ""
                          }}
                        </div>
                      </div>
                    </div>
                  </template>

                  <!-- Vehicle Info -->
                  <template v-slot:[`item.vehicle_info`]="{ item }">
                    <div>
                      <div class="font-weight-medium">
                        {{ item.vehicle_number || "Not provided" }}
                      </div>
                      <div class="text-caption text--secondary">
                        Type: {{ getVehicleType(item.vehicle_type) }}
                      </div>
                    </div>
                  </template>

                  <!-- Documents Status -->
                  <template v-slot:[`item.documents`]="{ item }">
                    <div class="d-flex flex-column">
                      <v-chip
                        x-small
                        :color="item.driving_license_path ? 'success' : 'grey'"
                        text-color="white"
                        class="mb-1"
                      >
                        <v-icon left x-small>
                          {{
                            item.driving_license_path
                              ? "mdi-check"
                              : "mdi-close"
                          }}
                        </v-icon>
                        License
                      </v-chip>
                      <v-chip
                        x-small
                        :color="item.rc_document_path ? 'success' : 'grey'"
                        text-color="white"
                        class="mb-1"
                      >
                        <v-icon left x-small>
                          {{
                            item.rc_document_path ? "mdi-check" : "mdi-close"
                          }}
                        </v-icon>
                        RC
                      </v-chip>
                      <v-chip
                        x-small
                        :color="
                          item.insurance_document_path ? 'success' : 'grey'
                        "
                        text-color="white"
                      >
                        <v-icon left x-small>
                          {{
                            item.insurance_document_path
                              ? "mdi-check"
                              : "mdi-close"
                          }}
                        </v-icon>
                        Insurance
                      </v-chip>
                    </div>
                  </template>

                  <!-- Expiry Status -->
                  <template v-slot:[`item.expiry_status`]="{ item }">
                    <div v-if="item.has_expired_documents">
                      <v-chip color="error" text-color="white" small>
                        <v-icon left small>mdi-alert</v-icon>
                        Expired
                      </v-chip>
                    </div>
                    <div
                      v-else-if="
                        item.expiring_documents &&
                        item.expiring_documents.length > 0
                      "
                    >
                      <v-chip color="warning" text-color="white" small>
                        <v-icon left small>mdi-clock-alert</v-icon>
                        Expiring Soon
                      </v-chip>
                    </div>
                    <div v-else>
                      <v-chip color="success" text-color="white" small>
                        <v-icon left small>mdi-check-circle</v-icon>
                        Valid
                      </v-chip>
                    </div>
                  </template>

                  <!-- Verification Status -->
                  <template v-slot:[`item.is_verified`]="{ item }">
                    <v-chip
                      :color="item.is_verified ? 'success' : 'warning'"
                      text-color="white"
                      small
                    >
                      <v-icon left small>
                        {{
                          item.is_verified
                            ? "mdi-check-circle"
                            : "mdi-clock-outline"
                        }}
                      </v-icon>
                      {{ item.is_verified ? "Verified" : "Pending" }}
                    </v-chip>
                  </template>

                  <!-- Actions -->
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="viewDetails(item)"
                    >
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="!item.is_verified"
                      icon
                      small
                      color="success"
                      @click="verifyDocument(item)"
                    >
                      <v-icon small>mdi-check-circle</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="!item.is_verified"
                      icon
                      small
                      color="error"
                      @click="openRejectDialog(item)"
                    >
                      <v-icon small>mdi-close-circle</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>

                <div v-else class="text-center pa-10">
                  <v-icon size="64" color="grey lighten-1"
                    >mdi-file-document-outline</v-icon
                  >
                  <p class="text-h6 grey--text text--lighten-1 mt-4">
                    No vehicle KYC documents found
                  </p>
                </div>
              </section>
            </v-card-text>

            <!-- Pagination -->
            <v-card-actions v-if="documents.length > 0">
              <v-spacer></v-spacer>
              <v-pagination
                v-model="page"
                :length="Math.ceil(totalCount / itemsPerPage)"
                :total-visible="7"
                @input="getData()"
                circle
              ></v-pagination>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Details Dialog -->
    <AppDialog
      :show.sync="detailsDialog"
      max-width="900"
      scrollable
      icon="mdi-file-document"
      title="Vehicle KYC Document Details"
      :subtitle="selectedDocument && selectedDocument.user ? `${selectedDocument.user.first_name} ${selectedDocument.user.last_name}` : ''"
    >
      <div v-if="selectedDocument">
          <v-row>
            <!-- User Information -->
            <v-col cols="12">
              <AppSectionHeader label="User Information" />
            </v-col>
            <v-col cols="6" sm="3">
              <AppInfoCard
                icon="mdi-account"
                label="Name"
                :value="selectedDocument.user ? `${selectedDocument.user.first_name} ${selectedDocument.user.last_name}` : 'N/A'"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <AppInfoCard icon="mdi-phone" label="Phone" :value="selectedDocument.user ? selectedDocument.user.phone : 'N/A'" />
            </v-col>
            <v-col cols="6" sm="3">
              <AppInfoCard icon="mdi-email" label="Email" :value="selectedDocument.user ? selectedDocument.user.email : 'N/A'" />
            </v-col>
            <v-col cols="6" sm="3">
              <AppInfoCard
                icon="mdi-badge-account"
                label="Position"
                :value="selectedDocument.user && selectedDocument.user.position ? selectedDocument.user.position.name : 'N/A'"
              />
            </v-col>

            <!-- Vehicle Information -->
            <v-col cols="12" class="mt-2">
              <AppSectionHeader label="Vehicle Information" />
            </v-col>
            <v-col cols="6">
              <AppInfoCard icon="mdi-numeric" label="Vehicle Number" :value="selectedDocument.vehicle_number || 'Not provided'" />
            </v-col>
            <v-col cols="6">
              <AppInfoCard icon="mdi-truck" label="Vehicle Type" :value="getVehicleType(selectedDocument.vehicle_type)" />
            </v-col>

            <!-- Documents -->
            <v-col cols="12" class="mt-2">
              <AppSectionHeader label="Documents" />
            </v-col>

            <!-- Driving License -->
            <v-col cols="12">
              <v-card outlined>
                <v-card-title class="subtitle-1">
                  <v-icon left>mdi-card-account-details</v-icon>
                  Driving License
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <div class="mb-2">
                        <span class="font-weight-medium">License Number:</span>
                        {{
                          selectedDocument.driving_license_number ||
                          "Not provided"
                        }}
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="mb-2">
                        <span class="font-weight-medium">Document:</span>
                        <v-btn
                          v-if="selectedDocument.driving_license_path"
                          text
                          small
                          color="primary"
                          @click="
                            viewDocument(selectedDocument.driving_license_path)
                          "
                        >
                          <v-icon left small>mdi-eye</v-icon>
                          View Document
                        </v-btn>
                        <span v-else class="error--text">Not uploaded</span>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- RC Document -->
            <v-col cols="12">
              <v-card outlined>
                <v-card-title class="subtitle-1">
                  <v-icon left>mdi-file-document</v-icon>
                  Registration Certificate (RC)
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <div class="mb-2">
                        <span class="font-weight-medium">RC Number:</span>
                        {{ selectedDocument.rc_number || "Not provided" }}
                      </div>
                      <div class="mb-2">
                        <span class="font-weight-medium">Expiry Date:</span>
                        {{ $options.filters.niceDate(selectedDocument.rc_expiry) || "Not provided" }}
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="mb-2">
                        <span class="font-weight-medium">Document:</span>
                        <v-btn
                          v-if="selectedDocument.rc_document_path"
                          text
                          small
                          color="primary"
                          @click="
                            viewDocument(selectedDocument.rc_document_path)
                          "
                        >
                          <v-icon left small>mdi-eye</v-icon>
                          View Document
                        </v-btn>
                        <span v-else class="error--text">Not uploaded</span>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Insurance Document -->
            <!-- <v-col cols="12">
              <v-card outlined>
                <v-card-title class="subtitle-1">
                  <v-icon left>mdi-shield-check</v-icon>
                  Insurance
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="6">
                      <div class="mb-2">
                        <span class="font-weight-medium">Policy Number:</span>
                        {{
                          selectedDocument.insurance_policy_number ||
                          "Not provided"
                        }}
                      </div>
                      <div class="mb-2">
                        <span class="font-weight-medium">Expiry Date:</span>
                        {{
                          selectedDocument.insurance_expiry || "Not provided"
                        }}
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="mb-2">
                        <span class="font-weight-medium">Document:</span>
                        <v-btn
                          v-if="selectedDocument.insurance_document_path"
                          text
                          small
                          color="primary"
                          @click="
                            viewDocument(
                              selectedDocument.insurance_document_path
                            )
                          "
                        >
                          <v-icon left small>mdi-eye</v-icon>
                          View Document
                        </v-btn>
                        <span v-else class="error--text">Not uploaded</span>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col> -->

            <!-- Verification Status -->
            <v-col cols="12" class="mt-2">
              <AppSectionHeader label="Verification Status" />
            </v-col>
            <v-col cols="12">
              <v-alert
                :type="selectedDocument.is_verified ? 'success' : 'warning'"
                border="left"
                colored-border
                elevation="2"
              >
                <div class="d-flex align-center">
                  <v-icon left>
                    {{
                      selectedDocument.is_verified
                        ? "mdi-check-circle"
                        : "mdi-clock-outline"
                    }}
                  </v-icon>
                  <span class="font-weight-medium">
                    {{
                      selectedDocument.is_verified
                        ? "Documents Verified"
                        : "Verification Pending"
                    }}
                  </span>
                </div>
                <div v-if="selectedDocument.verified_by" class="mt-2">
                  Verified by: {{ selectedDocument.verified_by.first_name }}
                  {{ selectedDocument.verified_by.last_name }} on
                  {{ selectedDocument.verified_at | niceDate }}
                </div>
                <div
                  v-if="selectedDocument.rejection_reason"
                  class="mt-2 error--text"
                >
                  <strong>Rejection Reason:</strong>
                  {{ selectedDocument.rejection_reason }}
                </div>
              </v-alert>
            </v-col>

            <!-- Expiring Documents Warning -->
            <v-col
              cols="12"
              v-if="
                selectedDocument.expiring_documents &&
                selectedDocument.expiring_documents.length > 0
              "
            >
              <v-alert
                type="warning"
                border="left"
                colored-border
                elevation="2"
              >
                <div class="font-weight-medium mb-2">
                  Documents Expiring Soon:
                </div>
                <ul>
                  <li
                    v-for="doc in selectedDocument.expiring_documents"
                    :key="doc.document"
                  >
                    {{ doc.document }} - Expires on {{ doc.expiry_date | niceDate }} ({{
                      doc.days_remaining
                    }}
                    days remaining)
                  </li>
                </ul>
              </v-alert>
            </v-col>

            <!-- Expired Documents Warning -->
            <v-col cols="12" v-if="selectedDocument.has_expired_documents">
              <v-alert type="error" border="left" colored-border elevation="2">
                <v-icon left>mdi-alert</v-icon>
                <span class="font-weight-medium"
                  >Some documents have expired. Please ask the user to update
                  them.</span
                >
              </v-alert>
            </v-col>
          </v-row>
      </div>

      <template v-slot:actions>
        <v-btn
          v-if="selectedDocument && !selectedDocument.is_verified"
          color="success"
          @click="verifyDocument(selectedDocument)"
        >
          <v-icon left>mdi-check-circle</v-icon>
          Verify Documents
        </v-btn>
        <v-btn
          v-if="selectedDocument && !selectedDocument.is_verified"
          color="error"
          @click="openRejectDialog(selectedDocument)"
        >
          <v-icon left>mdi-close-circle</v-icon>
          Reject
        </v-btn>
      </template>
    </AppDialog>

    <!-- Reject Dialog -->
    <v-dialog v-model="rejectDialog" max-width="500">
      <v-card>
        <v-card-title class="error white--text">
          <v-icon left color="white">mdi-close-circle</v-icon>
          Reject Documents
        </v-card-title>
        <v-card-text class="mt-4">
          <v-textarea
            v-model="rejectionReason"
            label="Rejection Reason"
            placeholder="Please provide a reason for rejection..."
            outlined
            rows="4"
            :rules="[(v) => !!v || 'Rejection reason is required']"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="rejectDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            @click="rejectDocument()"
            :disabled="!rejectionReason"
          >
            Reject
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";
import AppSectionHeader from "@/components/AppSectionHeader.vue";
import AppInfoCard from "@/components/AppInfoCard.vue";

export default {
  name: "VehicleKYCDocuments",
  components: {
    AppDialog,
    AppSectionHeader,
    AppInfoCard,
  },
  data() {
    return {
      isLoading: false,
      documents: [],
      totalCount: 0,
      page: 1,
      itemsPerPage: 10,
      filter: {
        search_keyword: null,
        is_verified: null,
        is_active: null,
        user_id: null,
      },
      headers: [
        { text: "User", value: "user", sortable: false },
        { text: "Vehicle Info", value: "vehicle_info", sortable: false },
        { text: "Documents", value: "documents", sortable: false },
        { text: "Expiry Status", value: "expiry_status", sortable: false },
        { text: "Verification", value: "is_verified", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      verificationStatuses: [
        { text: "Rejected", value: 2 },
        { text: "Verified", value: 1 },
        { text: "Pending", value: 0 },
      ],
      activeStatuses: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      vehicleTypes: [
        { text: "Bike", value: 1 },
        { text: "Car", value: 2 },
        { text: "Truck", value: 3 },
        { text: "Van", value: 4 },
        { text: "Other", value: 5 },
      ],
      detailsDialog: false,
      selectedDocument: null,
      rejectDialog: false,
      rejectionReason: "",
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
    };
  },
  created() {
    // Check if user_id is passed in route query
    if (this.$route.query.user_id) {
      this.filter.user_id = this.$route.query.user_id;
    }
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.itemsPerPage,
          ...this.filter,
        };

        const response = await axios.get("/vehicle-kyc-documents", { params });

        if (response.data.success) {
          this.documents = response.data.data;
          this.totalCount = response.data.count;
        }
      } catch (error) {
        console.error("Error fetching vehicle KYC documents:", error);
        this.showSnackbar("Error fetching data", "error");
      } finally {
        this.isLoading = false;
      }
    },

    viewDetails(document) {
      this.selectedDocument = document;
      this.detailsDialog = true;
    },

    async verifyDocument(document) {
      if (!document.all_documents_uploaded) {
        this.showSnackbar(
          "Cannot verify. All documents must be uploaded first.",
          "error"
        );
        return;
      }

      if (confirm("Are you sure you want to verify these documents?")) {
        try {
          const response = await axios.post(
            `/vehicle-kyc-documents/verify/${this.encryptIt(document.id)}`
          );

          if (response.data.success) {
            this.showSnackbar("Documents verified successfully", "success");
            this.getData();
            if (this.detailsDialog) {
              this.detailsDialog = false;
            }
          }
        } catch (error) {
          console.error("Error verifying document:", error);
          this.showSnackbar(
            error.response?.data?.sub_title || "Error verifying documents",
            "error"
          );
        }
      }
    },

    openRejectDialog(document) {
      this.selectedDocument = document;
      this.rejectionReason = "";
      this.rejectDialog = true;
      if (this.detailsDialog) {
        this.detailsDialog = false;
      }
    },

    async rejectDocument() {
      if (!this.rejectionReason) {
        this.showSnackbar("Please provide a rejection reason", "error");
        return;
      }

      try {
        const response = await axios.post(
          `/vehicle-kyc-documents/reject/${this.selectedDocument.id}`,
          {
            rejection_reason: this.rejectionReason,
          }
        );

        if (response.data.success) {
          this.showSnackbar("Documents rejected", "success");
          this.rejectDialog = false;
          this.getData();
        }
      } catch (error) {
        console.error("Error rejecting document:", error);
        this.showSnackbar(
          error.response?.data?.sub_title || "Error rejecting documents",
          "error"
        );
      }
    },

    viewDocument(path) {
      // Assuming documents are stored in public storage
      // const baseUrl = process.env.VUE_APP_API_URL || "http://localhost:8000";
      const documentUrl = `${this.mediaUrl}${path}`;
      window.open(documentUrl, "_blank");
    },

    getVehicleType(typeId) {
      const type = this.vehicleTypes.find((t) => t.value === typeId);
      return type ? type.text : "Not specified";
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    showSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    clearUserFilter() {
      this.filter.user_id = null;
      // Remove query parameter from URL
      this.$router.push({ path: this.$route.path });
      this.getData();
    },
  },
};
</script>

<style scoped>
.v-data-table >>> tbody tr:hover {
  background-color: #f5f5f5;
}
</style>
